import { createApi } from "@reduxjs/toolkit/query/react"
import { globalConfigs } from "configs"
import axios from "axios"
import { logOut, setTokens } from "../slices/authSlice"
import { clearAllMySelf } from "../slices/sliceMySelf"

const axiosBaseQuery =
  ({ baseUrl } = { baseUrl: "" }) =>
  async ({ url, method, body, params, headers }, { getState, signal }) => {
    try {
      const store = getState()
      const isPrivate = url.includes("private")

      const getAuthHeader = () => {
        if (store?.auth?.accessToken && isPrivate)
          return {
            Authorization: `Bearer ${store?.auth?.accessToken}`,
          }
        return {}
      }

      const result = await axios({
        url: url.startsWith("http") ? url : baseUrl + url,
        method: method ?? "GET",
        data: body,
        params,
        signal,
        withCredentials: false,
        headers: {
          ...getAuthHeader(),
          ...headers,
        },
      })
      return result
    } catch (axiosError) {
      const err = axiosError
      return {
        error: {
          status: err.response?.status ?? null,
          data: err.response?.data || err.message,
        },
      }
    }
  }

const axiosBaseQueryWithUrl = axiosBaseQuery({
  baseUrl: globalConfigs.publicApiBaseUrl,
})

const axiosBaseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await axiosBaseQueryWithUrl(args, api, extraOptions)
  const store = api.getState()
  const refreshToken = store?.auth?.refreshToken
  // Only a "private" URL ever had a bearer token attached in the first place
  // (see isPrivate above) — a 403 from a public endpoint (e.g. a wrong/expired
  // vendor-proposal PIN) is a real business error, not an expired session, so
  // retrying it as a token refresh would be pointless and can spuriously log
  // out whoever's actually signed in in this browser.
  if (result?.error?.status === 403 && args.url.includes("private")) {
    const refreshResult = await axiosBaseQueryWithUrl(
      {
        ...args,
        url: "/public/login/v1/refreshAccessToken",
        method: "POST",
        body: {
          refreshToken,
        },
      },
      api,
      extraOptions
    )

    if (refreshResult.error) {
      api.dispatch(clearAllMySelf())
      api.dispatch(logOut())
      api.dispatch()
    } else {
      api.dispatch(
        setTokens({
          accessToken: refreshResult.data.accessToken,
          refreshToken: refreshResult.data.refreshToken,
        })
      )
      result = await axiosBaseQueryWithUrl(args, api, extraOptions)
    }
  }

  return result
}

const axiosBaseQueryWithSSOLms = axiosBaseQuery({
  baseUrl: globalConfigs.ssoLmsBaseUrl,
})

export const api = createApi({
  baseQuery: axiosBaseQueryWithReauth,
  // Declared so per-endpoint providesTags/invalidatesTags actually drive cache
  // invalidation. Includes the existing procurement PR tags plus the V2 set.
  tagTypes: [
    "PR",
    "PR-DOCS",
    "PR-GRN",
    "PR-APPROVAL",
    "ORDER",
    "MYORDER",
    "SUPPLIER",
    "REQ-BUDGET",
    "MGR-BUDGET",
    "PROC-REPORT",
    "APPROVAL-RULE",
    "APPROVAL-INBOX",
    "ORDER-REVIEW",
    "ME-PERMISSIONS",
    "PURCHASE",
    "BUDGET-LINE",
    "BUDGET-CENTER",
    "GROUP-ITEM",
    "GROUP-ORDER",
    "VENDOR_PROPOSAL",
    "PURCHASE_VENDOR_PROPOSAL",
    "VENDOR_PROPOSAL_REVISION",
    "PROPOSAL_COMPARISON",
    "ITEM",
    "ITEM-GROUP",
    "ITEM-STOCK",
    "ITEM-AVAILABILITY",
    "ORDER-GROUP",
    "CENTER",
    "PROC-SUPPLIER",
    "PAYMENT_REQUEST",
    "ORDER-LIFECYCLE",
    "GRN",
  ],
  endpoints: () => ({}),
})
